/**
 * Admin Router component for multi-tenant admin dashboard navigation
 */

import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Support as SupportIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  History as HistoryIcon,
  Monitor as MonitorIcon,
  Campaign as CampaignIcon,
  MailOutline as MailOutlineIcon
} from '@mui/icons-material';
import AdminDashboard from './AdminDashboard';
import UserManagement from './UserManagement';
import SystemMonitoring from './SystemMonitoring';
import SupportCenter from './SupportCenter';
import SecurityManagement from './SecurityManagement';
import AuditLogs from './AuditLogs';
import SystemSettings from './SystemSettings';
import EmailManagement from './EmailManagement';
import MarketingLeads from './MarketingLeads';
import MarketingSubscriptions from './MarketingSubscriptions';

interface AdminRouterProps {
  onLogout?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  component: React.ComponentType<any>;
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    component: AdminDashboard
  },
  {
    id: 'users',
    label: 'User Management',
    icon: <PeopleIcon />,
    component: UserManagement
  },
  {
    id: 'support',
    label: 'Support Tickets',
    icon: <SupportIcon />,
    component: SupportCenter
  },
  {
    id: 'access-requests',
    label: 'Access Requests',
    icon: <CampaignIcon />,
    component: MarketingLeads
  },
  {
    id: 'marketing-list',
    label: 'Marketing list',
    icon: <MailOutlineIcon />,
    component: MarketingSubscriptions
  },
  {
    id: 'monitoring',
    label: 'System Monitoring',
    icon: <MonitorIcon />,
    component: SystemMonitoring
  },
  {
    id: 'audit',
    label: 'Audit Logs',
    icon: <HistoryIcon />,
    component: AuditLogs
  },
  {
    id: 'security',
    label: 'Security',
    icon: <SecurityIcon />,
    component: SecurityManagement
  },
  {
    id: 'email',
    label: 'Email Management',
    icon: <EmailIcon />,
    component: EmailManagement
  },
  {
    id: 'settings',
    label: 'System Settings',
    icon: <SettingsIcon />,
    component: SystemSettings
  }
];

const AdminRouter: React.FC<AdminRouterProps> = ({ onLogout }) => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const drawerWidth = 240;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (viewId: string) => {
    setCurrentView(viewId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const currentMenuItem = menuItems.find(item => item.id === currentView);
  const CurrentComponent = currentMenuItem?.component || AdminDashboard;

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={currentView === item.id}
              onClick={() => handleMenuClick(item.id)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {currentMenuItem?.label || 'Admin Panel'}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8 // Account for AppBar height
        }}
      >
        <CurrentComponent />
      </Box>
    </Box>
  );
};

export default AdminRouter;
